import type {
  RawProduct,
  RawImage,
  RawDefaultVariation,
  RawVariationGallery,
} from "../../../../../../types";
import type { TransformedProduct } from "../../../types/productApiTypes";

export interface DynamicProductDetails {
  hasDefaultVariation: boolean;
  stockStatus: "instock" | "outofstock" | "onbackorder";
  onBackorder: boolean;
  mainImage: RawImage | null;
  galleryImages: RawImage[];
  sku: string;
  minStock: number;
  maxStock: number;
  lowStockRemaining: number | null;
}

/**
 * A pure helper function to determine core product details that may change
 * based on the presence of a default variation (e.g., stock, SKU).
 *
 * @param product The raw product object from the API.
 * @param defaultVariation The raw default variation object, or null.
 * @param variationGalleryData The raw variation gallery data for the product.
 * @returns An object containing the final calculated details.
 */
export const getDynamicProductDetails = (
  product: RawProduct,
  defaultVariation: RawDefaultVariation | null,
  variationGalleryData: RawVariationGallery,
): DynamicProductDetails => {
  const hasDefaultVariation = !!defaultVariation;

  let stockStatus: TransformedProduct["stockStatus"] = "outofstock";
  let onBackorder = false;
  let mainImage: RawImage | null = product.images?.[0] || null;
  let galleryImages: RawImage[] = product.images?.slice(1) || [];
  let sku = product.sku || "";
  let minStock: number;
  let maxStock: number;
  let lowStockRemaining: number | null = null;

  // --- Image Logic ---
  // We ONLY parse the raw product's immediate images here.
  // We DO NOT merge the defaultVariation image anymore.
  // The UI imageResolver will handle the logic of picking variation vs parent images.
  if (product.type === "variation") {
    // If the entity itself is a variation, attach its specific custom gallery
    galleryImages = variationGalleryData.items || [];
  }

  // --- Stock, SKU, and Stock Limits Logic ---
  if (hasDefaultVariation) {
    stockStatus =
      defaultVariation.stock_status as TransformedProduct["stockStatus"];
    onBackorder = defaultVariation.is_on_backorder;
    lowStockRemaining = defaultVariation.low_stock_remaining;
    onBackorder = stockStatus === "onbackorder";
    sku = defaultVariation.sku;
    minStock = defaultVariation.min_stock;
    maxStock = defaultVariation.max_stock;

    // The default variation object in the extension might not carry low_stock_remaining
    // depending on API version/customization. Safest to default to null or check if it exists.
    // lowStockRemaining = defaultVariation.low_stock_remaining ?? null;
    // (Assuming standard Store API extension schema doesn't have it yet, keeping null).
    lowStockRemaining = null;
  } else {
    if (product.is_in_stock) {
      stockStatus = product.is_on_backorder ? "onbackorder" : "instock";
    }
    onBackorder = product.is_on_backorder;
    lowStockRemaining = product.low_stock_remaining;
    minStock = product.add_to_cart.minimum;
    maxStock = product.add_to_cart.maximum;
  }

  return {
    hasDefaultVariation,
    stockStatus,
    onBackorder,
    mainImage,
    galleryImages,
    sku,
    minStock,
    maxStock,
    lowStockRemaining,
  };
};
